/** «Editar partidos» de playoffs (2026-10-20): los partidos de playoffs no
 * tienen grupo, así que los selectores listan a TODOS los jugadores de la
 * categoría (primero los clasificados por seed, luego el resto A→Z) con
 * etiquetas que dicen su seed de playoff y su lugar de grupo
 * («S3 · 1.º Grupo A · Nombre»), en vez del «Rank#N» de inscripción. */

export interface PlayoffOptionInfo {
  seed: number | null;
  place: string | null;
}

/** «1.º Grupo A» → «1A»; «2.º Grupo 3» → «2-G3». */
export function shortPlaceTag(place: string | null | undefined): string | null {
  const found = (place ?? "").match(/^(\d+)\.º\s+(.+)$/);
  if (!found) return place ? place : null;
  const group = found[2].replace(/^Grupo\s+/i, "").trim();
  return /^[A-Za-z]$/.test(group) ? `${found[1]}${group.toUpperCase()}` : `${found[1]}-G${group}`;
}

/** Nombre PRIMERO (el select cerrado muestra el inicio y no debe esconder
 * al jugador): «Jerónimo Cortés · 1A · S1». */
export function playoffPlayerOptionLabel(name: string, info: PlayoffOptionInfo | null | undefined): string {
  const parts: string[] = [name];
  const tag = shortPlaceTag(info?.place);
  if (tag) parts.push(tag);
  if (info?.seed != null) parts.push(`S${info.seed}`);
  return parts.join(" · ");
}

export function sortPlayoffPlayerOptions<T extends { id: number }>(
  participants: readonly T[],
  infoFor: (id: number) => PlayoffOptionInfo | null | undefined,
  nameFor: (participant: T) => string,
): T[] {
  return [...participants].sort((a, b) => {
    const aSeed = infoFor(a.id)?.seed ?? Number.POSITIVE_INFINITY;
    const bSeed = infoFor(b.id)?.seed ?? Number.POSITIVE_INFINITY;
    if (aSeed !== bSeed) return aSeed - bSeed;
    return nameFor(a).localeCompare(nameFor(b), "es");
  });
}

/** Los selectores de jugador se bloquean mientras se guarda, si el partido ya
 * tiene resultado, o si es de grupos y no hay grupo elegido. Un partido de
 * playoffs NO necesita grupo. */
export function playerSelectDisabled(opts: {
  saving: boolean;
  isPlayoff: boolean;
  groupId: number;
  hasResult: boolean;
}): boolean {
  if (opts.saving || opts.hasResult) return true;
  return !opts.isPlayoff && !(opts.groupId > 0);
}

export type RegeneratePlayoffsState =
  | { enabled: true; reason: null }
  | { enabled: false; reason: string };

/** «Rehacer cuadro de playoffs»: solo mientras ningún partido de playoffs
 * tenga resultado. */
export function regeneratePlayoffsState(opts: {
  playoffMatchCount: number;
  hasPlayoffResults: boolean;
  busy: boolean;
}): RegeneratePlayoffsState {
  if (opts.playoffMatchCount === 0) return { enabled: false, reason: "Aún no hay cuadro de playoffs." };
  if (opts.hasPlayoffResults) {
    return { enabled: false, reason: "Ya hay resultados de playoffs: el cuadro ya no se puede rehacer." };
  }
  if (opts.busy) return { enabled: false, reason: "Espera a que termine la operación en curso." };
  return { enabled: true, reason: null };
}
